import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase";
import type { Role } from "./store";

export type Profile = {
  id: string;
  name: string;
  email: string;
  role: Role;
  /**
   * True for accounts created via scripts/create-admin-user.mjs (or any
   * future admin-provisioning script) with a known temporary password.
   * Drives the global "you must change your password" modal (see
   * ForcePasswordChangeModal) so a shared/temporary password is never
   * left in place past the very first login. Cleared automatically once
   * the user successfully sets their own password via
   * completeForcedPasswordChange().
   */
  must_change_password: boolean;
  /**
   * Max number of saved_charts rows this account may hold (see
   * supabase-schema.sql section 11 / enforce_chart_quota_trigger). null =
   * unlimited (staff/admin default, or an admin-upgraded customer).
   */
  chart_quota: number | null;
};

type AuthCtx = {
  /** null while the initial session is loading. */
  loading: boolean;
  session: Session | null;
  profile: Profile | null;
  login: (email: string, password: string) => Promise<{ error: string | null }>;
  register: (name: string, email: string, password: string) => Promise<{ error: string | null }>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: string | null }>;
  /**
   * Same as updatePassword, but also clears profiles.must_change_password
   * on success so the forced-change modal (see ForcePasswordChangeModal)
   * does not reappear on the next login. Used for the first-login forced
   * change flow instead of the password-recovery flow's updatePassword.
   */
  completeForcedPasswordChange: (newPassword: string) => Promise<{ error: string | null }>;
  /** True while a fresh login's profile has must_change_password = true. */
  forcedPasswordChangeActive: boolean;
  /**
   * True from the moment Supabase fires the `PASSWORD_RECOVERY` auth event
   * (user clicked the reset-password email link and landed back on the
   * site with a recovery token) until they successfully set a new
   * password or explicitly dismiss the flow. Drives the global
   * "Set a new password" modal mounted in main.tsx.
   */
  passwordRecoveryActive: boolean;
  dismissPasswordRecovery: () => void;
};

const Ctx = createContext<AuthCtx | null>(null);

async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select("id, name, email, role, must_change_password, chart_quota")
    .eq("id", userId)
    .single();
  if (error || !data) return null;
  return data as Profile;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [passwordRecoveryActive, setPasswordRecoveryActive] = useState(false);

  useEffect(() => {
    let active = true;

    // Load whatever session Supabase already has (e.g. from a previous
    // visit, persisted via its own storage) before wiring up live updates.
    supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return;
      setSession(data.session);
      if (data.session) setProfile(await fetchProfile(data.session.user.id));
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (!active) return;
      setSession(newSession);
      setProfile(newSession ? await fetchProfile(newSession.user.id) : null);

      // Fired when the user lands back on the site via the password-reset
      // email link (Supabase exchanges the recovery token for a session
      // and emits this event). Surface the "set new password" UI globally
      // — the user could land on any route, not just /login.
      if (event === "PASSWORD_RECOVERY") {
        setPasswordRecoveryActive(true);
      }
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const login: AuthCtx["login"] = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error ? error.message : null };
  };

  const register: AuthCtx["register"] = async (name, email, password) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      // Read by the handle_new_user trigger (raw_user_meta_data ->> 'name')
      // to seed public.profiles.name — see supabase-schema.sql.
      options: { data: { name } },
    });
    return { error: error ? error.message : null };
  };

  const logout = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword: AuthCtx["resetPassword"] = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/login`,
    });
    return { error: error ? error.message : null };
  };

  const updatePassword: AuthCtx["updatePassword"] = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (!error) setPasswordRecoveryActive(false);
    return { error: error ? error.message : null };
  };

  const completeForcedPasswordChange: AuthCtx["completeForcedPasswordChange"] = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return { error: error.message };

    // Clear the flag server-side so this modal never reappears, and
    // update local state immediately so the modal closes without waiting
    // for a full profile refetch.
    if (session) {
      const { error: profileError } = await supabase
        .from("profiles")
        .update({ must_change_password: false })
        .eq("id", session.user.id);
      if (profileError) return { error: profileError.message };
      setProfile((prev) => (prev ? { ...prev, must_change_password: false } : prev));
    }
    return { error: null };
  };

  const dismissPasswordRecovery = () => setPasswordRecoveryActive(false);

  const forcedPasswordChangeActive = !passwordRecoveryActive && !!profile?.must_change_password;

  const value = useMemo(
    () => ({
      loading,
      session,
      profile,
      login,
      register,
      logout,
      resetPassword,
      updatePassword,
      completeForcedPasswordChange,
      forcedPasswordChangeActive,
      passwordRecoveryActive,
      dismissPasswordRecovery,
    }),
    [loading, session, profile, passwordRecoveryActive, forcedPasswordChangeActive]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
